/**
 * Conversation trees — the gamified structure behind Ally's chats.
 *
 * A tree is a small DAG of prompts. Each node is one beat of a conversation:
 * something Ally reveals from the chart, something she asks, something she
 * reflects back, or a gate that opens a deeper branch once earlier beats are
 * done. Progress is per user per tree, and `depth` is the number the UI grows.
 *
 * WHY THE PROMPTS LIVE HERE AS DATA:
 *  - Cost. Every standard AI turn spends one of Base's 10 free daily messages
 *    or a Pro fair-use slot. The tree itself — titles, prompts, which node is
 *    open next, the progress ring — renders instantly with zero AI calls.
 *    Only the *reply* to a node costs anything, and only on a user tap. Never
 *    auto-fire a node on mount.
 *  - Grounding. 'reveal' nodes are narrated from engine output only. The AI
 *    never computes a position; it phrases values the engine already produced.
 *
 * Copy rules these prompts follow, and any new node must too: explain the
 * jargon inline or hang a glossary `term` off the node; say what a placement
 * MEANS, never what WILL happen; write for a 19-year-old with zero astrology
 * background.
 */

import type { AstroSystem } from './horoscope.js';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/**
 * Which lens a tree runs in. The two traditions set `data-mode` and pick up the
 * tradition palette; 'social' and 'self' are mode-neutral.
 */
export type TreeSystem = AstroSystem | 'social' | 'self';

export type TreeNodeKind =
  /** Ally asks the user something. Their answer is the substance. */
  | 'question'
  /** Ally reflects their earlier answers back through the chart. */
  | 'reflection'
  /** Ally states something the ENGINE computed. Deterministic; AI only phrases it. */
  | 'reveal'
  /** A gate. Completing it opens a deeper branch — the reward beat. */
  | 'unlock';

export const TREE_NODE_KINDS: TreeNodeKind[] = ['question', 'reflection', 'reveal', 'unlock'];

export interface TreeNode {
  id: string;
  /** What Ally says or asks at this beat. Sent as context, not shown raw when it reads as a stage direction. */
  prompt: string;
  kind: TreeNodeKind;
  /** Short chip label for the tree map. Falls back to a truncated prompt. */
  label?: string;
  /**
   * Node ids that must all be completed first. AUTHORITATIVE for gating —
   * `unlocks` is the mirrored forward edge, kept for drawing the map.
   */
  requires?: string[];
  /** Node ids this one opens. Mirrors their `requires`; see `validateTree`. */
  unlocks?: string[];
  /**
   * This beat can end in a person suggestion. Only ever acted on when the user
   * has `socialEnabled` — the social layer is off by default and no
   * recommendation may be produced, shown or even computed while it is off.
   */
  leadsToRecommendation?: boolean;
  /** Glossary term id explaining the jargon in this node (app/src/features/glossary/terms.ts). */
  term?: string;
}

export interface ConversationTree {
  id: string;
  title: string;
  /** One line under the title: what the user gets out of walking this path. */
  blurb: string;
  system: TreeSystem;
  nodes: TreeNode[];
}

export interface TreeProgress {
  uid: string;
  treeId: string;
  completedNodeIds: string[];
  /** The node the user is on now. Empty string when the tree is finished. */
  currentNodeId: string;
  startedAtISO: string;
  completedAtISO?: string;
  /** Longest completed prerequisite chain — how deep they actually got, not how many taps. */
  depth: number;
}

// ---------------------------------------------------------------------------
// Tree 1 — Vedic path
// ---------------------------------------------------------------------------

export const VEDIC_TREE: ConversationTree = {
  id: 'vedic-path',
  title: 'The Vedic road',
  blurb: 'Indian astrology reads your Moon first, not your Sun. Start there and see how far the map goes.',
  system: 'vedic',
  nodes: [
    {
      id: 'vedic.moon-sign',
      kind: 'reveal',
      label: 'Your Moon sign',
      term: 'moon-sign',
      unlocks: ['vedic.nakshatra', 'vedic.dasha-now', 'vedic.lagna'],
      prompt:
        'In Indian astrology your Moon sign — where the Moon sat when you were born — is the headline, not your Sun sign. It is treated as a lens on your inner weather: how you settle, what makes you feel safe. Here is yours, and what that lens is said to emphasise.',
    },
    {
      id: 'vedic.nakshatra',
      kind: 'reveal',
      label: 'Your nakshatra',
      term: 'nakshatra',
      requires: ['vedic.moon-sign'],
      unlocks: ['vedic.nakshatra-feel'],
      prompt:
        'Zoom in. The sky is also cut into 27 lunar mansions called nakshatras — think of them as sub-signs, each about 13 degrees wide, each with its own character. Yours narrows that Moon sign down a lot.',
    },
    {
      id: 'vedic.nakshatra-feel',
      kind: 'question',
      label: 'Does it fit?',
      requires: ['vedic.nakshatra'],
      unlocks: ['vedic.yoga-gate'],
      prompt:
        'Be honest with me — how much of that actually sounds like you? Tell me the part that fits and the part that misses. It is a lens, not a verdict, and the misses tell me more than the hits.',
    },
    {
      id: 'vedic.dasha-now',
      kind: 'reveal',
      label: 'The chapter you are in',
      term: 'dasha',
      requires: ['vedic.moon-sign'],
      unlocks: ['vedic.dasha-story'],
      prompt:
        'Vedic astrology splits a life into long chapters called dashas, each one "run" by a different planet, and which chapter you start in comes from your nakshatra. This is the one you are in right now and roughly how long it has left.',
    },
    {
      id: 'vedic.dasha-story',
      kind: 'reflection',
      label: 'What changed',
      requires: ['vedic.dasha-now'],
      unlocks: ['vedic.yoga-gate'],
      prompt:
        'Your last chapter handed over around the date I just gave you. Think back to then — what was going on? I will show you what this tradition says that handover EMPHASISES, and you decide whether it matches your own memory of it.',
    },
    {
      id: 'vedic.lagna',
      kind: 'reveal',
      label: 'Your rising sign',
      term: 'lagna',
      requires: ['vedic.moon-sign'],
      unlocks: ['vedic.gochara'],
      prompt:
        'Your lagna, or rising sign, is whichever sign was climbing over the horizon at your birth minute. It rotates through all twelve in a day, so it needs an accurate birth time — if yours is a guess I will say so instead of pretending.',
    },
    {
      id: 'vedic.gochara',
      kind: 'reveal',
      label: 'Where things sit now',
      term: 'gochara',
      requires: ['vedic.lagna'],
      unlocks: ['vedic.what-now'],
      prompt:
        'Gochara just means the planets where they are today, counted from your Moon sign rather than from a generic zodiac. Here is what is currently sitting where, and which part of life this tradition reads that against.',
    },
    {
      id: 'vedic.yoga-gate',
      kind: 'unlock',
      label: 'Deeper: combinations',
      requires: ['vedic.nakshatra-feel', 'vedic.dasha-story'],
      unlocks: ['vedic.yoga'],
      prompt:
        'You have told me enough that the next layer is worth opening. Ready to look at the combinations in your chart — the named patterns classical texts get excited about?',
    },
    {
      id: 'vedic.yoga',
      kind: 'reveal',
      label: 'Your combinations',
      term: 'yoga',
      requires: ['vedic.yoga-gate'],
      unlocks: ['vedic.what-now'],
      prompt:
        'A yoga here is a named combination — two or three placements in a specific relationship that classical texts gave a name to. These are the ones your chart forms, in plain English, including the awkward ones. No chart is only good news and I am not going to pretend yours is.',
    },
    {
      id: 'vedic.what-now',
      kind: 'question',
      label: 'What to do with it',
      requires: ['vedic.gochara', 'vedic.yoga'],
      prompt:
        'You have the whole map now. What do you want to actually do with it — understand a specific stretch of your life, time something you have been putting off, or just keep reading? Tell me and I will point you at the right part.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Tree 2 — Western path
// ---------------------------------------------------------------------------

export const WESTERN_TREE: ConversationTree = {
  id: 'western-path',
  title: 'The Western road',
  blurb: 'Sun, Moon, Rising and the angles between your planets — the system most of the internet is using.',
  system: 'western',
  nodes: [
    {
      id: 'western.big-three',
      kind: 'reveal',
      label: 'Your big three',
      term: 'rising-sign',
      unlocks: ['western.rising-check', 'western.elements', 'western.aspect-tightest'],
      prompt:
        'The famous three: Sun (what you are driving at), Moon (how you process it), Rising (the version of you that walks into a room first). Here are yours and what each one is read as emphasising.',
    },
    {
      id: 'western.rising-check',
      kind: 'question',
      label: 'The room test',
      term: 'birth-time',
      requires: ['western.big-three'],
      unlocks: ['western.depth-gate'],
      prompt:
        'Rising signs are the easiest thing to sanity-check, because it is about first impressions. Does the description above match what people say about you before they know you? If it is way off, your birth time may be off — that is fixable.',
    },
    {
      id: 'western.elements',
      kind: 'reveal',
      label: 'Your balance',
      term: 'elements',
      requires: ['western.big-three'],
      unlocks: ['western.saturn'],
      prompt:
        'Every sign belongs to one of four elements — fire, earth, air, water — and most charts lean hard one way. Here is your spread, including whatever you have almost none of, which is usually the more interesting half.',
    },
    {
      id: 'western.aspect-tightest',
      kind: 'reveal',
      label: 'Your tightest aspect',
      term: 'aspect',
      requires: ['western.big-three'],
      unlocks: ['western.aspect-feel'],
      prompt:
        'An aspect is just the angle between two planets — 90 degrees, 120 degrees, and so on — and the tighter the angle is to exact, the louder that pairing is read as being. This is the tightest one in your chart.',
    },
    {
      id: 'western.aspect-feel',
      kind: 'question',
      label: 'Name the tension',
      term: 'orb',
      requires: ['western.aspect-tightest'],
      unlocks: ['western.depth-gate'],
      prompt:
        'That pairing describes a pull between two things you want. Put it in your own words — where in your life do those two actually collide? Your version is more useful to me than the textbook one.',
    },
    {
      id: 'western.saturn',
      kind: 'reveal',
      label: 'Your Saturn',
      term: 'saturn-return',
      requires: ['western.elements'],
      unlocks: ['western.transit-now'],
      prompt:
        'Saturn takes about 29 years to go round, so it comes back to its birth position around 29 and again around 58. Astrologers read that stretch as the growing-up bill arriving. Here is where yours sits and when the return lands for you.',
    },
    {
      id: 'western.transit-now',
      kind: 'reveal',
      label: 'What is live now',
      term: 'retrograde',
      requires: ['western.saturn'],
      unlocks: ['western.what-lands'],
      prompt:
        'These are the angles currently forming between today sky and your birth chart, with the dates they are exact. Read them as themes that are loud right now — not as a schedule of events.',
    },
    {
      id: 'western.depth-gate',
      kind: 'unlock',
      label: 'Deeper: your timeline',
      requires: ['western.rising-check', 'western.aspect-feel'],
      unlocks: ['western.progressions'],
      prompt:
        'You have given me enough to open the slower, more personal layer. Want to see how your chart is read as having moved since you were born?',
    },
    {
      id: 'western.progressions',
      kind: 'reveal',
      label: 'Your progressed chart',
      term: 'progressions',
      requires: ['western.depth-gate'],
      unlocks: ['western.what-lands'],
      prompt:
        'Progressions use a symbolic shortcut: one day after your birth stands in for one year of your life. So your chart on day 24 is read as the you of age 24. Here is where yours has drifted to and what that shift is said to emphasise.',
    },
    {
      id: 'western.what-lands',
      kind: 'question',
      label: 'What landed',
      requires: ['western.transit-now', 'western.progressions'],
      prompt:
        'Out of everything in this path, what actually landed and what felt like a horoscope column? Tell me either way — rating it is how the readings get sharper for you specifically.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Tree 3 — Social / compatibility path
// ---------------------------------------------------------------------------

export const SOCIAL_TREE: ConversationTree = {
  id: 'social-path',
  title: 'You and other people',
  blurb: 'How two charts get compared, what that can and cannot tell you, and whether you want any of it switched on.',
  system: 'social',
  nodes: [
    {
      id: 'social.intent',
      kind: 'question',
      label: 'What you want',
      unlocks: ['social.dealbreakers', 'social.synastry-101'],
      prompt:
        'Before any chart talk: what are you actually after right now — something romantic, real friends, people to build with, or just someone to argue about astrology with? There is no wrong answer and you can change it whenever.',
    },
    {
      id: 'social.dealbreakers',
      kind: 'question',
      label: 'Your lines',
      requires: ['social.intent'],
      unlocks: ['social.optin-gate'],
      prompt:
        'What are your hard lines — the things that end it for you regardless of how well two charts read together? I will treat these as filters, not as preferences to be talked out of.',
    },
    {
      id: 'social.synastry-101',
      kind: 'reveal',
      label: 'How two charts compare',
      term: 'synastry',
      requires: ['social.intent'],
      unlocks: ['social.milan-101'],
      prompt:
        'Synastry is the Western method: lay two charts over each other and look at the angles between your planets and theirs. It describes the FLAVOUR of a dynamic — where it is easy, where it grinds. It cannot tell you whether someone is good to you. Only they can show you that.',
    },
    {
      id: 'social.milan-101',
      kind: 'reveal',
      label: 'The 36-point system',
      term: 'kundali-milan',
      requires: ['social.synastry-101'],
      unlocks: ['social.venus-mars'],
      prompt:
        'The Indian method scores eight categories out of 36 points, mostly off the two Moon positions. You will see it quoted as a pass mark at family gatherings. Here is what each category is actually measuring, and why a number out of 36 is a summary, not a fate.',
    },
    {
      id: 'social.venus-mars',
      kind: 'reveal',
      label: 'Your Venus and Mars',
      requires: ['social.milan-101'],
      unlocks: ['social.optin-gate'],
      prompt:
        'Venus is read as what you are drawn to; Mars as how you go after it. Together they are the shorthand most compatibility work leans on. Here are yours, and the friction your own two are read as producing before anyone else is in the picture.',
    },
    {
      id: 'social.optin-gate',
      kind: 'unlock',
      label: 'Turn connections on?',
      term: 'connection-request',
      requires: ['social.dealbreakers', 'social.venus-mars'],
      unlocks: ['social.compat-read'],
      prompt:
        'Everything so far was about you. Going further means other people can be matched with you, which is off by default and stays off until you say otherwise. Nothing about you is visible to anyone right now. Want to turn it on? You can turn it back off any time and go invisible again.',
    },
    {
      id: 'social.compat-read',
      kind: 'reveal',
      label: 'Reading a match',
      term: 'compatibility-score',
      leadsToRecommendation: true,
      requires: ['social.optin-gate'],
      unlocks: ['social.first-move'],
      prompt:
        'Here is how to read a compatibility card without over-trusting it: the number is a deterministic score from both charts and both sets of intentions, the strengths are where the two charts agree, and the frictions are the parts worth knowing early. It is a conversation starter, not a screening result.',
    },
    {
      id: 'social.first-move',
      kind: 'reflection',
      label: 'Opening line',
      leadsToRecommendation: true,
      requires: ['social.compat-read'],
      unlocks: ['social.boundaries'],
      prompt:
        'If you want, I will help you open. Not a pickup line — something honest that references what you two actually have in common. You send it, in your words. I never message anyone for you.',
    },
    {
      id: 'social.boundaries',
      kind: 'question',
      label: 'Staying safe',
      requires: ['social.first-move'],
      prompt:
        'Last one, and it matters more than any chart: what would make you leave a conversation? Naming it now makes it much easier to actually do. Blocking and reporting are one tap away and the other person is never told.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Tree 4 — Self-knowledge path
// ---------------------------------------------------------------------------

export const SELF_TREE: ConversationTree = {
  id: 'self-path',
  title: 'Getting to know yourself',
  blurb: 'No jargon required. Answer a few honest questions and watch your chart get used as a mirror rather than a fortune.',
  system: 'self',
  nodes: [
    {
      id: 'self.today-word',
      kind: 'question',
      label: 'One word',
      unlocks: ['self.energy-drain', 'self.moon-mirror'],
      prompt:
        'One word for how today has actually been. Not the version you would post — the real one. That is the whole first step.',
    },
    {
      id: 'self.energy-drain',
      kind: 'question',
      label: 'The drain',
      requires: ['self.today-word'],
      unlocks: ['self.pattern'],
      prompt:
        'What has been quietly taking the most out of you lately? It is usually not the obvious big thing — it is the small thing you keep re-deciding.',
    },
    {
      id: 'self.moon-mirror',
      kind: 'reveal',
      label: 'Your Moon, as a mirror',
      term: 'moon-sign',
      requires: ['self.today-word'],
      unlocks: ['self.pattern'],
      prompt:
        'Both traditions read the Moon as how you handle your own feelings — what settles you, what you do when you are overloaded. Here is yours, framed as a description to argue with rather than a label to accept.',
    },
    {
      id: 'self.pattern',
      kind: 'reflection',
      label: 'The pattern',
      requires: ['self.energy-drain', 'self.moon-mirror'],
      unlocks: ['self.value'],
      prompt:
        'Here is the overlap between what you told me and what your chart is read as emphasising. Where they line up, that is worth sitting with. Where they do not, your version wins — you are the one living it.',
    },
    {
      id: 'self.value',
      kind: 'question',
      label: 'What you protect',
      requires: ['self.pattern'],
      unlocks: ['self.deep-gate'],
      prompt:
        'What is the one thing you would protect even when it costs you something? Knowing it makes almost every other decision smaller.',
    },
    {
      id: 'self.deep-gate',
      kind: 'unlock',
      label: 'Deeper: the hard half',
      requires: ['self.value'],
      unlocks: ['self.shadow', 'self.journal-prompt'],
      prompt:
        'The next part is the less flattering half of the same traits — the cost side. It is more useful than the nice version, but only if you are up for it. Open it?',
    },
    {
      id: 'self.shadow',
      kind: 'reflection',
      label: 'The cost side',
      requires: ['self.deep-gate'],
      unlocks: ['self.year-ahead'],
      prompt:
        'Every strength has a bill. The same placements read as your best qualities are read as their overuse too. Here is the cost side of yours, said plainly and without the doom.',
    },
    {
      id: 'self.journal-prompt',
      kind: 'question',
      label: 'Write it down',
      requires: ['self.deep-gate'],
      unlocks: ['self.year-ahead'],
      prompt:
        'Save this somewhere you will see it again. What is one sentence from this conversation you want in your journal, dated today? Future you gets to check whether it held up.',
    },
    {
      id: 'self.year-ahead',
      kind: 'reveal',
      label: 'Your year ahead',
      term: 'solar-return',
      requires: ['self.shadow', 'self.journal-prompt'],
      prompt:
        'A solar return is the chart cast for the exact moment the Sun returns to its birth position — your astrological birthday, usually a day either side of the calendar one. Astrologers read it as the theme of the year. Here is yours, as themes to watch for, not events to wait on.',
    },
  ],
};

/** All four paths, in the order the tree picker shows them. */
export const CONVERSATION_TREES: ConversationTree[] = [SELF_TREE, VEDIC_TREE, WESTERN_TREE, SOCIAL_TREE];

// ---------------------------------------------------------------------------
// Lookup and progress
// ---------------------------------------------------------------------------

export function treeById(treeId: string): ConversationTree | undefined {
  return CONVERSATION_TREES.find((t) => t.id === treeId);
}

export function nodeById(tree: ConversationTree, nodeId: string): TreeNode | undefined {
  return tree.nodes.find((n) => n.id === nodeId);
}

/** Root nodes — everything with no prerequisites. */
export function rootNodes(tree: ConversationTree): TreeNode[] {
  return tree.nodes.filter((n) => (n.requires ?? []).length === 0);
}

/**
 * How deep a node sits: 1 for a root, otherwise one more than its deepest
 * prerequisite. Cycle-safe (a cycle resolves to the depth reached so far),
 * though `validateTree` should have rejected the cycle long before this.
 */
export function nodeDepth(tree: ConversationTree, nodeId: string): number {
  const memo = new Map<string, number>();
  const visiting = new Set<string>();
  const walk = (id: string): number => {
    const cached = memo.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return 0;
    const node = nodeById(tree, id);
    if (!node) return 0;
    visiting.add(id);
    let deepest = 0;
    for (const req of node.requires ?? []) {
      deepest = Math.max(deepest, walk(req));
    }
    visiting.delete(id);
    const depth = deepest + 1;
    memo.set(id, depth);
    return depth;
  };
  return walk(nodeId);
}

/** Deepest chain the user has actually completed. This is `TreeProgress.depth`. */
export function progressDepth(tree: ConversationTree, completedNodeIds: string[]): number {
  let deepest = 0;
  for (const id of completedNodeIds) deepest = Math.max(deepest, nodeDepth(tree, id));
  return deepest;
}

/** Open right now: every prerequisite completed, and not itself completed. */
export function availableNodes(tree: ConversationTree, progress: TreeProgress): TreeNode[] {
  const done = new Set(progress.completedNodeIds);
  return tree.nodes.filter(
    (n) => !done.has(n.id) && (n.requires ?? []).every((req) => done.has(req)),
  );
}

export function isNodeAvailable(tree: ConversationTree, nodeId: string, progress: TreeProgress): boolean {
  return availableNodes(tree, progress).some((n) => n.id === nodeId);
}

/** 0..1 — completed nodes over total. The progress ring. */
export function treeCompletion(tree: ConversationTree, progress: TreeProgress): number {
  if (tree.nodes.length === 0) return 0;
  const ids = new Set(tree.nodes.map((n) => n.id));
  const done = progress.completedNodeIds.filter((id) => ids.has(id)).length;
  return done / tree.nodes.length;
}

export function startTreeProgress(uid: string, tree: ConversationTree, startedAtISO: string): TreeProgress {
  return {
    uid,
    treeId: tree.id,
    completedNodeIds: [],
    currentNodeId: rootNodes(tree)[0]?.id ?? '',
    startedAtISO,
    depth: 0,
  };
}

/**
 * Mark a node done and move on. Idempotent: completing the same node twice
 * neither double-counts nor advances twice. `currentNodeId` becomes the
 * shallowest newly-available node, or '' when the tree is finished.
 */
export function completeTreeNode(
  tree: ConversationTree,
  progress: TreeProgress,
  nodeId: string,
  atISO: string,
): TreeProgress {
  const completedNodeIds = progress.completedNodeIds.includes(nodeId)
    ? progress.completedNodeIds
    : [...progress.completedNodeIds, nodeId];
  const next: TreeProgress = {
    ...progress,
    completedNodeIds,
    currentNodeId: '',
    depth: progressDepth(tree, completedNodeIds),
  };
  const open = availableNodes(tree, next);
  if (open.length === 0) {
    return { ...next, completedAtISO: progress.completedAtISO ?? atISO };
  }
  let shallowest = open[0];
  for (const node of open) {
    if (nodeDepth(tree, node.id) < nodeDepth(tree, shallowest.id)) shallowest = node;
  }
  return { ...next, currentNodeId: shallowest.id };
}

/**
 * Structural problems in a tree, as human-readable strings. Empty means clean.
 * Meant for a unit test over `CONVERSATION_TREES`, not for runtime.
 */
export function validateTree(tree: ConversationTree): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  for (const node of tree.nodes) {
    if (ids.has(node.id)) problems.push(`${tree.id}: duplicate node id "${node.id}"`);
    ids.add(node.id);
  }
  for (const node of tree.nodes) {
    for (const req of node.requires ?? []) {
      if (!ids.has(req)) problems.push(`${tree.id}: "${node.id}" requires unknown node "${req}"`);
    }
    for (const target of node.unlocks ?? []) {
      if (!ids.has(target)) {
        problems.push(`${tree.id}: "${node.id}" unlocks unknown node "${target}"`);
        continue;
      }
      const other = nodeById(tree, target);
      if (!(other?.requires ?? []).includes(node.id)) {
        problems.push(`${tree.id}: "${node.id}" unlocks "${target}" but "${target}" does not require it`);
      }
    }
  }
  // Mirror the other way: every prerequisite should advertise what it opens.
  for (const node of tree.nodes) {
    for (const req of node.requires ?? []) {
      const other = nodeById(tree, req);
      if (other && !(other.unlocks ?? []).includes(node.id)) {
        problems.push(`${tree.id}: "${node.id}" requires "${req}" but "${req}" does not list it in unlocks`);
      }
    }
  }
  if (rootNodes(tree).length === 0) problems.push(`${tree.id}: no root node (every node has prerequisites)`);
  // Cycle detection over `requires`.
  const state = new Map<string, 'open' | 'closed'>();
  const walk = (id: string): boolean => {
    const seen = state.get(id);
    if (seen === 'closed') return false;
    if (seen === 'open') return true;
    state.set(id, 'open');
    for (const req of nodeById(tree, id)?.requires ?? []) {
      if (ids.has(req) && walk(req)) return true;
    }
    state.set(id, 'closed');
    return false;
  };
  for (const node of tree.nodes) {
    if (walk(node.id)) {
      problems.push(`${tree.id}: prerequisite cycle reaching "${node.id}"`);
      break;
    }
  }
  return problems;
}
